// ─────────────────────────────────────────────────────────────────────────────
// audio_engine.ts — Single shared audio backbone
//
// Topology:
//
//     Instruments / textures / samples
//                  │
//                  ▼
//            named channels (input → output GainNode pair)
//                  │
//                  ▼
//                mixer      (sum of all channel outputs)
//                  │
//                  ▼
//          effects rack     (cutoff, reverb, delay — bypass by default)
//                  │
//                  ▼
//               master       (global volume; mono enforced)
//                  │
//                  ▼
//            ctx.destination
//
// Channels today:
//   'instrument' — synthesised tones (cast, stylus, charge, resolves)
//   'texture'    — sustained loops (wand drone, future pen scratching)
//
// Channels are registered by the engine; instruments connect into a
// channel by id. Channel and instrument concepts are deliberately
// independent — any module can play into any channel without the
// channel needing to know what's feeding it. New channels can be added
// later without changing instruments, and instruments can be swapped
// without changing channels.
//
// Audio config: mono output, 44.1 kHz target sample rate. Web Audio
// always renders internally at 32-bit float; the "16-bit feel" is
// achieved at the channel-count and synthesis-complexity level rather
// than via API (no API knob for output bit depth).
//
// Lazy init: the AudioContext is created on first use to satisfy
// browser autoplay policy. Subsequent calls reuse the same context;
// suspended contexts are resumed automatically.
// ─────────────────────────────────────────────────────────────────────────────

import { AudioChannel } from './audio_channel';
import { AudioMixer } from './audio_mixer';
import { EffectsRack } from './audio_effects_rack';
import { DrumInstrument } from './audio_drum';
import { TextureInstrument } from './audio_texture';

const TARGET_SAMPLE_RATE = 44100;
const DEFAULT_MASTER_GAIN = 0.25;

/** Built-in channel ids. New ids can be passed to `channel()` and the
 *  engine will register them on demand. */
export type ChannelId = 'instrument' | 'texture' | string;

const DEFAULT_CHANNEL_IDS: ChannelId[] = ['instrument', 'texture'];

export class AudioEngine {
  private _ctx: AudioContext | null = null;
  private _master: GainNode | null = null;
  private _mixer: AudioMixer | null = null;
  private _rack: EffectsRack | null = null;
  private _channels = new Map<string, AudioChannel>();
  private _drum: DrumInstrument | null = null;
  private _texture: TextureInstrument | null = null;
  private _dead = false;

  // ── Lazy bring-up ────────────────────────────────────────────────────

  /** Returns the live AudioContext, building the engine on first call.
   *  Returns null if Web Audio is unavailable on this environment. */
  context(): AudioContext | null {
    if (this._dead) return null;
    if (this._ctx) {
      if (this._ctx.state === 'suspended') {
        this._ctx.resume().catch(() => { /* user-gesture required */ });
      }
      return this._ctx;
    }
    try {
      this._ctx = new AudioContext({ sampleRate: TARGET_SAMPLE_RATE });
    } catch {
      this._dead = true;
      return null;
    }
    this._build();
    return this._ctx;
  }

  private _build(): void {
    const ctx = this._ctx!;

    // Mixer ← channels → effects → master → destination
    this._mixer = new AudioMixer(ctx);
    this._rack  = new EffectsRack(ctx);
    this._master = ctx.createGain();
    this._master.gain.value = DEFAULT_MASTER_GAIN;

    // Mono enforcement at the master: incoming signal is downmixed to a
    // single channel here, regardless of what individual nodes produce.
    this._master.channelCount = 1;
    this._master.channelCountMode = 'explicit';
    this._master.channelInterpretation = 'speakers';

    this._mixer.output.connect(this._rack.input);
    this._rack.output.connect(this._master).connect(ctx.destination);

    // Pre-register the default channels so they're available immediately.
    for (const id of DEFAULT_CHANNEL_IDS) this._registerChannel(id);
  }

  private _registerChannel(id: string): AudioChannel {
    const ch = new AudioChannel(this._ctx!, id);
    this._mixer!.add(ch);
    this._channels.set(id, ch);
    return ch;
  }

  // ── Public surface ───────────────────────────────────────────────────

  /** Get a channel by id, creating it if it doesn't exist yet. Returns
   *  null when Web Audio is unavailable. */
  channel(id: ChannelId): AudioChannel | null {
    if (!this.context()) return null;
    return this._channels.get(id) ?? this._registerChannel(id);
  }

  /** Effects rack — cutoff, reverb, delay (all bypass-by-default). */
  rack(): EffectsRack | null {
    if (!this.context()) return null;
    return this._rack;
  }

  /** Default drum instrument on the 'instrument' channel. Built lazily.
   *  Used for tap-down/tap-release feedback (see canvas_app pointer
   *  pipeline). */
  drum(): DrumInstrument | null {
    const ctx = this.context();
    if (!ctx) return null;
    if (!this._drum) {
      const ch = this.channel('instrument');
      if (!ch) return null;
      this._drum = new DrumInstrument(ctx, ch);
    }
    return this._drum;
  }

  /** Default texture instrument on the 'texture' channel. Built lazily.
   *  Hosts both pen-scratch (intensity-driven) and hum (cast tension)
   *  modes — environmental events flip between them via setHum/reset. */
  texture(): TextureInstrument | null {
    const ctx = this.context();
    if (!ctx) return null;
    if (!this._texture) {
      const ch = this.channel('texture');
      if (!ch) return null;
      this._texture = new TextureInstrument(ctx, ch);
    }
    return this._texture;
  }

  /** Global volume. */
  setMasterVolume(v: number): void {
    if (!this._master || !this._ctx) return;
    this._master.gain.setTargetAtTime(v, this._ctx.currentTime, 0.01);
  }
}

// ── Singleton ───────────────────────────────────────────────────────────────

/** Process-wide engine. Modules import this and call `.channel()` /
 *  `.rack()` directly. Lazy — no AudioContext is created until something
 *  is actually played. */
export const audioEngine = new AudioEngine();
